/*
 * Voyado price range — dual-thumb slider visuals.
 * -----------------------------------------------
 * Adapted from the original assets/plp-filters-drawer-price-range.js.
 * Same clamp (min ≤ max) + track repaint + live label positioning, but the
 * range nodes are injected dynamically by voyado-facet-renderer.js, so this
 * exposes window.initVoyadoPriceRange(root) for the controller to call after
 * the facets render. The commit ({min,max}) is fired by the renderer's own
 * 'change' listener — this file only handles the visual clamp/repaint.
 *
 * Hooks: [data-plp-filters-drawer-price-range],
 *        [data-plp-filters-drawer-price-thumb-min/max],
 *        [data-plp-filters-drawer-price-track],
 *        [data-plp-filters-drawer-price-display-min/max].
 */
(function () {
  'use strict';

  // One active observer at a time (a single price facet per drawer). Disconnect
  // the previous one before re-observing so a detached range node (replaced on
  // each facet rebuild) doesn't keep an observer registered.
  let activeRO = null;
  let currentPaint = null;

  function init(root) {
    if (!root || root.dataset.voyadoRangeInit === 'true') return;
    const minThumb = root.querySelector('[data-plp-filters-drawer-price-thumb-min]');
    const maxThumb = root.querySelector('[data-plp-filters-drawer-price-thumb-max]');
    const track = root.querySelector('[data-plp-filters-drawer-price-track]');
    const displayMin = root.querySelector('[data-plp-filters-drawer-price-display-min]');
    const displayMax = root.querySelector('[data-plp-filters-drawer-price-display-max]');
    if (!minThumb || !maxThumb) return;
    root.dataset.voyadoRangeInit = 'true';

    const rangeMin = Number(root.dataset.rangeMin) || 0;
    const rangeMax = Number(root.dataset.rangeMax) || 0;
    const span = rangeMax - rangeMin || 1;
    // Converted markets: rescale the Voyado-scale slider value into the real market currency for
    // DISPLAY only (window.broraVoyado.priceScale, published by voyado-price-storefront.js from the
    // card prices). The thumb value + commit stay on the Voyado scale, so filtering is unaffected.
    // Either way the value goes through the shared formatter, so the bounds carry the market's own
    // currency and digit grouping. Returns an HTML string (formatMoneyHtml adds the AED dirham
    // glyph); written via innerHTML below.
    // The rescale is a median estimate and lands up to ~1% inside the products a bound should reach,
    // which shows where there is no minor unit to absorb it. `atBound` widens the two resting ends by
    // this much and rounds them outwards; a value the shopper dragged to is shown as it is.
    const SCALE_TOLERANCE = 0.015;

    function label(v, round, atBound) {
      const bv = window.broraVoyado || {};
      let n = v;
      if (bv.priceScale) {
        n = v / bv.priceScale;
        if (atBound) n *= round === Math.ceil ? 1 + SCALE_TOLERANCE : 1 - SCALE_TOLERANCE;
      }
      // noDecimals=true: the slider bounds are always whole numbers, so drop the trailing ".00".
      const scaled = bv.priceScale ? round(n) : v;
      const currency = bv.priceScale
        ? bv.currencyCode
        : ((window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || bv.currencyCode);
      const fmt = bv.formatMoneyHtml || bv.formatMoney;
      if (typeof fmt === 'function') {
        const out = fmt(scaled, currency, true);
        if (out) return out;
      }
      try {
        return currency
          ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency, maximumFractionDigits: 0 }).format(scaled)
          : String(scaled);
      } catch (e) {
        return String(scaled);
      }
    }

    const THUMB_WIDTH = 24;
    const LABEL_MIN_GAP = 4;

    function thumbCenterPx(pct, containerWidth) {
      return (pct / 100) * (containerWidth - THUMB_WIDTH) + THUMB_WIDTH / 2;
    }

    function idealLeft(pct, centerX, labelWidth, containerWidth) {
      if (pct <= 0) return 0;
      if (pct >= 100) return containerWidth - labelWidth;
      const left = centerX - labelWidth / 2;
      if (left < 0) return 0;
      if (left + labelWidth > containerWidth) return containerWidth - labelWidth;
      return left;
    }

    function positionLabels(minPct, maxPct, minCenter, maxCenter, containerWidth) {
      const minW = displayMin ? displayMin.offsetWidth : 0;
      const maxW = displayMax ? displayMax.offsetWidth : 0;
      let minLeft = idealLeft(minPct, minCenter, minW, containerWidth);
      let maxLeft = idealLeft(maxPct, maxCenter, maxW, containerWidth);
      const overlap = (minLeft + minW + LABEL_MIN_GAP) - maxLeft;
      if (overlap > 0) {
        let pushMin = overlap / 2;
        let pushMax = overlap - pushMin;
        if (minLeft - pushMin < 0) { pushMax += pushMin - minLeft; pushMin = minLeft; }
        const maxRoom = containerWidth - maxW - maxLeft;
        if (pushMax > maxRoom) { pushMin += pushMax - maxRoom; pushMax = maxRoom; }
        minLeft -= pushMin;
        maxLeft += pushMax;
      }
      if (displayMin) { displayMin.style.left = minLeft + 'px'; displayMin.style.transform = 'none'; }
      if (displayMax) { displayMax.style.left = maxLeft + 'px'; displayMax.style.transform = 'none'; }
    }

    // N-16: positionLabels reads displayMin/Max.offsetWidth,
    // which depends on the NEW label text — the write (innerHTML) has to happen before that read
    // can be correct, so the read-after-write itself isn't avoidable here (the new width isn't
    // knowable without it). What IS avoidable is forcing that reflow synchronously mid-script:
    // splitting the innerHTML/track writes and the offsetWidth-dependent read across two rAF
    // frames lets the browser's own natural layout pass satisfy the read, instead of an
    // out-of-band forced one (measured up to 32.7ms per call before this split).
    let paintScheduled = false;
    function paint() {
      if (paintScheduled) return;
      paintScheduled = true;
      requestAnimationFrame(function () {
        paintScheduled = false;
        const minVal = Number(minThumb.value);
        const maxVal = Number(maxThumb.value);
        const minPct = ((minVal - rangeMin) / span) * 100;
        const maxPct = ((maxVal - rangeMin) / span) * 100;
        const containerWidth = root.offsetWidth;
        const minCenter = thumbCenterPx(minPct, containerWidth);
        const maxCenter = thumbCenterPx(maxPct, containerWidth);
        if (track) {
          track.style.marginLeft = minCenter + 'px';
          track.style.width = (maxCenter - minCenter) + 'px';
        }
        if (displayMin) displayMin.innerHTML = label(minVal, Math.floor, minVal === rangeMin);
        if (displayMax) displayMax.innerHTML = label(maxVal, Math.ceil, maxVal === rangeMax);
        requestAnimationFrame(function () {
          positionLabels(minPct, maxPct, minCenter, maxCenter, containerWidth);
        });
      });
    }

    if (typeof ResizeObserver === 'function') {
      if (activeRO) activeRO.disconnect();
      activeRO = new ResizeObserver(paint);
      activeRO.observe(root);
    }
    minThumb.addEventListener('input', function () {
      if (Number(minThumb.value) > Number(maxThumb.value)) minThumb.value = maxThumb.value;
      paint();
    });
    maxThumb.addEventListener('input', function () {
      if (Number(maxThumb.value) < Number(minThumb.value)) maxThumb.value = minThumb.value;
      paint();
    });
    currentPaint = paint;
    paint();
  }

  // The real-currency scale lands async (voyado-price-storefront.js fetches card prices), so repaint
  // the active range's labels when it's published.
  document.addEventListener('voyado:price-scale', function () { if (currentPaint) currentPaint(); });

  window.initVoyadoPriceRange = function (scope) {
    const ctx = scope || document;
    ctx.querySelectorAll('[data-plp-filters-drawer-price-range]').forEach(init);
  };
})();
